import _ from 'lodash'
import { DossError } from './error.js'

type OrderedObject = {
  id?: string
  order?: number | null
}

/**
 * Error class for bad order
 * Meaning: "you tried to insert an object in a position that is already taken"
 */
export class OrderError extends DossError {
  constructor(message: string) {
    super(message, {
      errCode: 'bad_order',
      // Deliberately not a user error. `checkAllOrdersAreUnique` inspects objects already stored,
      // so a duplicate order there means our data is inconsistent, not that a caller asked wrongly.
      isUserError: false,
      isException: false,
    })
  }
}

/**
 * Type guard that checks that the specified property used for ordering is a valid number
 */
function orderPropertyIsNumber<T, K extends keyof T>(
  item: T,
  property: K,
): item is T & { [P in K]: number } {
  return _.isFinite(item[property])
}

/**
 * Checks if the given order is valid for the given list of objects.
 *
 * @throws {OrderError} if the order of `targetObject` is invalid
 */
export function checkValidOrder<T extends OrderedObject, K extends keyof T>(
  allObjects: T[],
  targetObjectOrder?: number | null,
  targetObjectId?: string,
  orderProperty: K = 'order' as K,
): void {
  // Check if there is another object with the same order that isn't the same object
  const duplicateObjectOrder = allObjects.find((object) => {
    const sameSpot = object[orderProperty] === targetObjectOrder
    const sameObject = targetObjectId !== undefined ? targetObjectId === object.id : false
    return sameSpot && !sameObject
  })

  if (duplicateObjectOrder) {
    const nextAvailableOrder = getNextOrder(allObjects, orderProperty)
    // If there is already an object with the order of the new given object, it isn't valid. New
    // objects can only be inserted in a place there isn't already one
    throw new OrderError(
      `Cannot insert a new object at order ${targetObjectOrder}. Next available order is ${nextAvailableOrder}`,
    )
  }
}

/**
 * Checks if all the orders are unique
 */
export function checkAllOrdersAreUnique<T extends OrderedObject, K extends keyof T>(
  objects: T[],
  orderProperty: K = 'order' as K,
): void {
  const orders: number[] = objects.map((object) => {
    if (!orderPropertyIsNumber(object, orderProperty)) {
      throw new Error('Object has invalid order')
    }
    return object[orderProperty]
  })

  const uniqueOrders: Set<number> = new Set(orders)

  if (orders.length !== uniqueOrders.size) {
    throw new OrderError('Ordered objects must have unique orders')
  }
}

/**
 * Gets the next order for the given list of orders.
 */
export function getNextOrder<T extends OrderedObject, K extends keyof T>(
  objects: T[],
  orderProperty: K = 'order' as K,
): number {
  const orders: number[] = objects.map((object) => {
    if (!orderPropertyIsNumber(object, orderProperty)) {
      throw new Error('Object has invalid order')
    }
    return object[orderProperty]
  })

  // Use Math.max to find the highest order
  const latestOrder = Math.max(...orders, 0)
  return latestOrder + 1
}

/**
 * Calculates and returns a new array of items reordered based on the specified new order
 * for a given item. This function ensures that the order of all other items is adjusted to
 * accommodate the change without modifying their relative order, except for making space
 * for the item's new position.
 */
export function getReorderedItems<T extends OrderedObject, K extends keyof T>(
  items: T[],
  itemIdentifier: string,
  newOrder: number,
  orderProperty: K = 'order' as K,
): T[] {
  const validatedItems = items.map((item) => {
    if (!orderPropertyIsNumber(item, orderProperty)) {
      throw new Error(
        `Property ${String(orderProperty)} is not a number for item with id ${item.id}`,
      )
    }
    return item
  })

  const currentItem = validatedItems.find((item) => item.id === itemIdentifier)

  if (!currentItem) {
    throw new Error(`Could not find item with id ${itemIdentifier}`)
  }

  const oldOrder = currentItem[orderProperty]

  if (oldOrder === newOrder) {
    // return normalized values to ensure no gaps and unique order values
    return _.chain(validatedItems)
      .orderBy((item) => item[orderProperty])
      .map((item, index) => ({ ...item, [orderProperty]: index + 1 }))
      .value()
  }

  const sortedItems = _.orderBy(validatedItems, (item) => item[orderProperty])

  // Adjust orders for all items
  const reorderedItems = sortedItems.map((item) => {
    let newOrderValue = item[orderProperty] as number

    if (item.id === itemIdentifier) {
      newOrderValue = newOrder // Directly set the new order for the current item
    } else if (oldOrder < newOrder) {
      // If the item is moved to a higher order, decrement orders between old and new
      // (inclusive of old, exclusive of new)
      if (item[orderProperty] > oldOrder && item[orderProperty] <= newOrder) {
        newOrderValue = newOrderValue - 1
      }
    } else if (oldOrder > newOrder) {
      // If the item is moved to a lower order, increment orders between new and old
      // (inclusive of new, exclusive of old)
      if (item[orderProperty] >= newOrder && item[orderProperty] < oldOrder) {
        newOrderValue = newOrderValue + 1
      }
    }

    return { ...item, [orderProperty]: newOrderValue }
  })

  // Final normalization to ensure no gaps and unique order values
  return _.chain(reorderedItems)
    .orderBy((item) => item[orderProperty])
    .map((item, index) => ({ ...item, [orderProperty]: index + 1 }))
    .value()
}

type WorkspaceView = {
  id: string
  createdAt: Date
  updatedAt: Date
  parentWorkspaceId: string
  order: number
  rowNumber: number
}

/**
 * Validate that in an ordered list of views, the row number is always ascending
 */
function _validateRelativeOrderBetweenRows(views: WorkspaceView[], beforeSort: boolean): void {
  const sortedByOrder = _.orderBy(views, 'order')
  for (let i = 0; i < sortedByOrder.length - 1; i++) {
    if (sortedByOrder[i].rowNumber > sortedByOrder[i + 1].rowNumber) {
      throw new Error(
        `Row numbers are not in ascending order ${beforeSort ? 'before' : 'after'} sorting`,
      )
    }
  }
}

/**
 * Takes a list of views with order and rowNumber and makes sure there are no gaps in rowNumber
 */
function _normalizeRowNumbers(views: WorkspaceView[]): WorkspaceView[] {
  const sortedViews = _.sortBy(views, ({ order }) => order)
  const rows: Record<string, WorkspaceView[]> = {}
  sortedViews.forEach((view) => {
    rows[view.rowNumber] ||= []
    rows[view.rowNumber].push(view)
  })
  const normalizedViews: WorkspaceView[] = []
  Object.keys(rows).forEach((rowNumber, index) => {
    rows[rowNumber].forEach((view) => {
      normalizedViews.push({ ...view, rowNumber: index })
    })
  })
  return normalizedViews
}

/**
 *
 * Reordering views in a workspace is a 2 x 2 matrix of where a view can come from and where it can land
 * A view can either land in 1) a row by itself or 2) a row with other views
 * A row can either come from 1) a row by itself or 2) a row with other views
 *
 * This reorder function first branches off based on where the view will land,
 * because the logic is similar if the target is the same.
 *
 * If the view is landing in a (new) row by itself, we need to:
 * 1) Infer the new order of the view by getting the largest order in the previous row (and some edge cases)
 * 2) Essentially reorder the rows by bumping the row number of the rows below the new row by 1
 * We can identify the rows that are "below" the new row by comparing the order of views.
 * All the views in the rows below the new row should have an order value that is larger or equal to the inferred new order.
 * Note: If the view was coming from its own row, there will be gaps in the row numbers, but we will normalize it at the end
 * 3) Reorder the orders of the all views it is a flattened list
 *
 * If the view is landing in a row with other views, we need to:
 * 1) Update the row number of the view being moved
 * 2) Reorder the orders of all the views
 * No need to reorder rows! Just need to remove the gap if the view was coming from its own row.
 *
 */
export function getReorderedWorkspaceViews(
  views: WorkspaceView[],
  viewId: string,
  newRowNumber: number,
  newOrder?: number,
): WorkspaceView[] {
  const currentView = views.find((view) => view.id === viewId)
  if (!currentView) {
    throw new Error(`Could not find view with id ${viewId}`)
  }
  _validateRelativeOrderBetweenRows(views, true)

  const sortedFlattenedViews = _.orderBy(views, 'order')
  const viewsByRow = _.groupBy(sortedFlattenedViews, 'rowNumber')
  const allRowNumbers = views.reduce((acc, view) => {
    acc.add(view.rowNumber)
    return acc
  }, new Set<number>())

  let inferredNewOrder: number
  let viewsWithUpdatedRowNumbers: WorkspaceView[]
  // Handle the case where the view is landing in a (new) row by itself (no new order provided)
  if (_.isNil(newOrder)) {
    // Since no new "order" is provided, infer the new order based on the new row number
    if (newRowNumber === 0) {
      // If the row number is 0, the new order is 0
      inferredNewOrder = 0
    } else if (newRowNumber >= Math.max(...Array.from(allRowNumbers))) {
      // If the row number is larger than largest row number, the new order is the largest order + 1
      inferredNewOrder = Math.max(...sortedFlattenedViews.map((view) => view.order)) + 1
    } else {
      // The row above should technically always be 1 less than the new row number
      // Because that is how the drop monitor gets the new row number
      // BUT we will do this anyways to be safe from gaps
      const rowAboveCurrentRow = Array.from(allRowNumbers).reduce((acc, rowNumber) => {
        if (rowNumber < newRowNumber && rowNumber > acc) {
          return rowNumber
        }
        return acc
      }, 0)
      const rowAboveCurrentRowMaxOrder = Math.max(
        ...viewsByRow[rowAboveCurrentRow].map((view) => view.order),
      )
      inferredNewOrder = rowAboveCurrentRowMaxOrder + 1
    }

    // Essentially reorder the rows by bumping the row number of the views in the row below the new row by 1
    viewsWithUpdatedRowNumbers = sortedFlattenedViews.map((view) => {
      if (view.id === viewId) {
        return {
          ...view,
          rowNumber: newRowNumber,
        }
      }
      // Exclusive of what's above and inclusive of what's below because we always add 1 to the smaller number to get the new order
      if (view.order >= inferredNewOrder) {
        view.rowNumber = view.rowNumber + 1
      }
      return view
    })
  } else {
    // Handle the case where the view is landing in a row with other views
    // Update the row number of the view being moved
    viewsWithUpdatedRowNumbers = sortedFlattenedViews.map((view) => {
      if (view.id === viewId) {
        return {
          ...view,
          rowNumber: newRowNumber,
        }
      }
      return view
    })
  }

  // Both branches need to reorder the orders of all the views
  const reorderedViews = viewsWithUpdatedRowNumbers.map((view) => {
    const newOrderOfMovingItem = newOrder ?? inferredNewOrder
    if (view.id === viewId) {
      return {
        ...view,
        order: newOrderOfMovingItem,
      }
    }
    if (view.order >= newOrderOfMovingItem) {
      return {
        ...view,
        order: view.order + 1,
      }
    }
    return view
  })

  // Normalize the orders
  const sortedReorderedView = [...reorderedViews].sort((a, b) => a.order - b.order)
  const orderNormalizedViews = sortedReorderedView.map((view, index) => {
    return {
      ...view,
      order: index,
    }
  })

  // Normalize row numbers
  const normalizedViews = _normalizeRowNumbers(orderNormalizedViews)
  _validateRelativeOrderBetweenRows(normalizedViews, false)

  return normalizedViews
}

/**
 * A javascript sort function comparitor that sorts objects by their order property
 * in ascending order.
 */
export function byAscOrder<T extends OrderedObject, K extends keyof T>(
  a: T,
  b: T,
  orderProperty: K = 'order' as K,
): number {
  if (!orderPropertyIsNumber(a, orderProperty) || !orderPropertyIsNumber(b, orderProperty)) {
    throw new Error('Object has invalid order')
  }

  return a[orderProperty] - b[orderProperty]
}

/**
 * A javascript sort function comparitor that sorts objects by their order property
 * in descending order.
 */
export function byDescOrder<T extends OrderedObject, K extends keyof T>(
  a: T,
  b: T,
  orderProperty: K = 'order' as K,
): number {
  if (!orderPropertyIsNumber(a, orderProperty) || !orderPropertyIsNumber(b, orderProperty)) {
    throw new Error('Object has invalid order')
  }

  return b[orderProperty] - a[orderProperty]
}

/**
 * Given a list of ordered items and an index to insert a new item at, return the new order
 * that can be passed to `getReorderedItems` to insert the new item at the specified index.
 *
 * Note: Index is 0-based and order is 1-based (in most cases).
 * The orderedItems may not have consecutive order values, so the new order value may not be
 * the same as the index or off by 1 from the index.
 */
export function getNewOrderByInsertionIndex<T extends { [key in K]: number }, K extends keyof T>(
  orderedItems: T[],
  newIndex: number,
  orderProperty: K,
): number {
  // Find the correct order of the dragged item
  let newOrder
  if (orderedItems.length === 0) {
    // If the parent has no children, the new order is 1
    newOrder = 1
  } else if (newIndex >= orderedItems.length) {
    // If the dragged item is moved to the end, the index is the length of the children
    // This will also handle the case where there's some mismatched cache and the tree showed more items than there actually are
    newOrder = orderedItems[orderedItems.length - 1][orderProperty] + 1
  } else if (newIndex === 0) {
    // If the dragged item is moved to the beginning, the index is 0
    if (orderedItems[0][orderProperty] <= 0) {
      // Note: The fallback is 0 rather than 1 because we still have some 0-ordered items in the db.
      // This will allow us to handle inserting a new item at the beginning of the list when the current first item is 0
      // In this case, it is okay to pass 0 to getReorderedItems because it will normalize the order values
      newOrder = 0
    } else if (orderedItems[0][orderProperty] === 1) {
      newOrder = 1
    } else {
      newOrder = orderedItems[0][orderProperty] - 1
    }
  } else {
    // for all other cases, we get the order of the item currently at the index specified by the newPosition
    // when getReorderItems receive a new order that conflicts with an existing order,
    // it will bump the current item in that order to the next order
    newOrder = orderedItems[newIndex][orderProperty]
  }

  return newOrder
}
